import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";
import { Save, FileText } from "lucide-react";

export default function ConfigMeta() {
  const { form, set, save, loading } = useSettings();
  if (!form) return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;

  const previewTitle = form.meta_title || form.site_name;
  const previewDesc = form.meta_description || "";
  // Derive preview domain: prefer admin-set site_url, otherwise default to maxlive88.com
  const rawUrl = form.site_url || form.canonical_url || "";
  let previewUrl = "maxlive88.com";
  try {
    if (rawUrl) {
      const u = new URL(rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`);
      previewUrl = u.hostname.replace(/^www\./, "");
    }
  } catch {
    previewUrl = "maxlive88.com";
  }

  return (
    <div data-testid="config-meta-page">
      <PageHeader title="Configuration" subtitle="Meta Data · SEO titles, descriptions and social previews" />
      <ConfigTabs />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
        <form onSubmit={(e) => { e.preventDefault(); save(); }} className="lg:col-span-2 space-y-5" data-testid="config-meta-form">
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
              <FileText size={14} className="text-blue-500" />
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Search Engine (SEO)</div>
            </div>
            <div className="space-y-4">
              <Field label="Site URL (canonical)" hint="Used in outbound emails (password reset etc.) and SEO. Example: https://maxlive88.com">
                <input
                  value={form.site_url || ""}
                  onChange={(e) => set("site_url", e.target.value)}
                  placeholder="https://maxlive88.com"
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
                  data-testid="cfg-meta-site-url"
                />
              </Field>
              <Field label="Meta Title" hint={`${(form.meta_title || "").length}/60 characters recommended`}>
                <input value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="cfg-meta-title" />
              </Field>
              <Field label="Meta Description" hint={`${(form.meta_description || "").length}/160 characters recommended`}>
                <textarea rows={3} value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm" data-testid="cfg-meta-desc" />
              </Field>
              <Field label="Meta Keywords" hint="Comma-separated">
                <input value={form.meta_keywords || ""} onChange={(e) => set("meta_keywords", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" />
              </Field>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
              <FileText size={14} className="text-purple-500" />
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Open Graph / Social</div>
            </div>
            <div className="space-y-4">
              <Field label="OG Title">
                <input value={form.og_title || ""} onChange={(e) => set("og_title", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" placeholder="Falls back to Meta Title" />
              </Field>
              <Field label="OG Description">
                <textarea rows={3} value={form.og_description || ""} onChange={(e) => set("og_description", e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm" placeholder="Falls back to Meta Description" />
              </Field>
              <Field label="Twitter Handle">
                <input value={form.twitter_handle || ""} onChange={(e) => set("twitter_handle", e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" placeholder="@yourbrand" />
              </Field>
            </div>
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={loading} className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="cfg-meta-save">
              <Save size={14} /> {loading ? "Saving..." : "Save Meta Data"}
            </button>
          </div>
        </form>

        {/* Preview */}
        <div className="space-y-4" data-testid="meta-preview">
          <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Live Preview</div>

          <div className="bg-white border border-gray-200 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Google Search</div>
            <div className="space-y-0.5">
              <div className="text-xs text-gray-600">{previewUrl}</div>
              <div className="text-base text-blue-700 hover:underline cursor-pointer line-clamp-1">{previewTitle}</div>
              <div className="text-xs text-gray-600 line-clamp-2">{previewDesc || "No description set."}</div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 p-3 pb-0">Facebook / LinkedIn</div>
            <div className="p-3">
              {form.og_image_url ? (
                <img src={form.og_image_url} alt="OG" className="w-full aspect-[1200/630] object-cover rounded-sm bg-gray-100 mb-2" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="w-full aspect-[1200/630] bg-gray-100 rounded-sm mb-2 flex items-center justify-center text-xs text-gray-400">OG image not set</div>
              )}
              <div className="text-[10px] uppercase text-gray-500">{previewUrl}</div>
              <div className="text-sm font-semibold text-gray-900 line-clamp-2">{form.og_title || previewTitle}</div>
              <div className="text-xs text-gray-600 line-clamp-2 mt-1">{form.og_description || previewDesc}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</label>
        {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
